import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Calendar,
  Plus,
  Trash2,
  Edit2,
  Printer,
  Clock,
  BookOpen,
  Building,
  User,
  Filter,
  X,
  FileText,
  ChevronDown,
  ChevronUp,
  Save,
  Eye,
  Check
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const STREAMS = ['Interior Design', 'Fashion Design', 'MBA Design Management', 'Skill Enhancement'];
const SEMESTERS_BY_STREAM = {
  'Interior Design': ['Semester 1','Semester 2','Semester 3','Semester 4','Semester 5','Semester 6','Semester 7','Semester 8'],
  'Fashion Design': ['Semester 1','Semester 2','Semester 3','Semester 4','Semester 5','Semester 6','Semester 7','Semester 8'],
  'MBA Design Management': ['PG Sem 1','PG Sem 2','PG Sem 3','PG Sem 4'],
  'Skill Enhancement': ['Semester 1','Semester 2','Semester 3','Semester 4','Semester 5','Semester 6','Semester 7','Semester 8']
};

const genAcademicYears = () => {
  const y = new Date().getFullYear();
  return [-1,0,1,2].map(i => `${y+i}-${(y+i+1).toString().slice(2)}`);
};

const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-IN', { weekday:'short', day:'2-digit', month:'short', year:'numeric' });
};

const fmtDay = (d) => {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { weekday:'long' });
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  return `${hr > 12 ? hr-12 : hr === 0 ? 12 : hr}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
};

const ExamTimetable = () => {
  const [entries, setEntries] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [studentView, setStudentView] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [termTexts, setTermTexts] = useState({});
  const [termInput, setTermInput] = useState('');

  const [filters, setFilters] = useState({
    academic_year: genAcademicYears()[1],
    semester_type: '',
    stream: '',
    semester: ''
  });

  // Bulk form
  const [header, setHeader] = useState({
    academic_year: genAcademicYears()[1],
    semester_type: 'Even',
    stream: '',
    semester: ''
  });
  const [rows, setRows] = useState([]);

  const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const h = hdrs();
      const [sRes, cRes, empRes] = await Promise.all([
        axios.get(`${API}/api/subjects`, { headers: h }),
        axios.get(`${API}/api/classrooms`, { headers: h }),
        axios.get(`${API}/api/exam-timetable/invigilators`, { headers: h })
      ]);
      setSubjects(Array.isArray(sRes.data) ? sRes.data : []);
      setClassrooms(Array.isArray(cRes.data) ? cRes.data : []);
      setTeachers(Array.isArray(empRes.data) ? empRes.data : []);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEntries = async () => {
    try {
      const params = {};
      if (filters.academic_year) params.academic_year = filters.academic_year;
      if (filters.semester_type) params.semester_type = filters.semester_type;
      if (filters.stream) params.stream = filters.stream;
      if (filters.semester) params.semester = filters.semester;
      const res = await axios.get(`${API}/api/exam-timetable`, { headers: hdrs(), params });
      setEntries(Array.isArray(res.data) ? res.data : []);
    } catch { setEntries([]); }
  };

  useEffect(() => { fetchData().then(fetchEntries); }, [fetchData]);
  useEffect(() => { if (!loading) fetchEntries(); }, [filters]);

  // Auto-populate rows when stream+semester selected
  const populateSubjects = (stream, semester) => {
    const filtered = subjects.filter(s => {
      if (s.stream !== stream) return false;
      if (stream === 'Skill Enhancement') return true;
      return !semester || s.semester === semester;
    });
    return filtered.sort((a, b) => a.name.localeCompare(b.name)).map(s => ({
      subject_id: s.id,
      subject_name: s.name,
      exam_date: '',
      start_time: '09:00',
      end_time: '12:00',
      exam_type: s.subject_type === 'Practical' ? 'Practical' : 'Theory',
      classroom_id: '',
      classroom_name: '',
      invigilator_id: '',
      invigilator_name: '',
      notes: ''
    }));
  };

  const handleStreamSemChange = (field, value) => {
    const newHeader = { ...header, [field]: value };
    if (field === 'stream') { newHeader.semester = ''; setRows([]); }
    setHeader(newHeader);
    if (newHeader.stream && newHeader.semester) {
      setRows(populateSubjects(newHeader.stream, newHeader.semester));
    }
  };

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'classroom_id') {
        const c = classrooms.find(x => x.id === value);
        updated[idx].classroom_name = c ? c.name : '';
      }
      if (field === 'invigilator_id') {
        const t = teachers.find(x => x.id === value);
        updated[idx].invigilator_name = t ? t.name : '';
      }
      return updated;
    });
  };

  const resetForm = () => {
    setHeader({ academic_year: filters.academic_year || genAcademicYears()[1], semester_type: filters.semester_type || 'Even', stream: filters.stream || '', semester: filters.semester || '' });
    setRows([]);
    setShowForm(false);
  };

  const handleBulkSubmit = async () => {
    const valid = rows.filter(r => r.exam_date);
    if (valid.length === 0) { toast.error('Please fill dates for at least one subject'); return; }
    for (const r of valid) {
      if (r.start_time >= r.end_time) { toast.error(`${r.subject_name}: End time must be after start time`); return; }
    }
    setSaving(true);
    let ok = 0; const errs = [];
    for (const r of valid) {
      try {
        await axios.post(`${API}/api/exam-timetable`, { ...header, ...r }, { headers: hdrs() });
        ok++;
      } catch (e) {
        errs.push(`${r.subject_name}: ${e.response?.data?.detail || 'Error'}`);
      }
    }
    setSaving(false);
    if (ok > 0) toast.success(`${ok} exam(s) scheduled`);
    errs.forEach(e => toast.error(e));
    if (ok > 0) { resetForm(); fetchEntries(); }
  };

  // Inline edit
  const startEdit = (entry) => {
    setEditingId(entry.id);
    setEditForm({ exam_date: entry.exam_date, start_time: entry.start_time, end_time: entry.end_time, exam_type: entry.exam_type, classroom_id: entry.classroom_id || '', classroom_name: entry.classroom_name || '', invigilator_id: entry.invigilator_id || '', invigilator_name: entry.invigilator_name || '', notes: entry.notes || '' });
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(null); };
  const updateEditField = (field, value) => {
    setEditForm(prev => {
      const u = { ...prev, [field]: value };
      if (field === 'classroom_id') { const c = classrooms.find(x => x.id === value); u.classroom_name = c ? c.name : ''; }
      if (field === 'invigilator_id') { const t = teachers.find(x => x.id === value); u.invigilator_name = t ? t.name : ''; }
      return u;
    });
  };
  const saveEdit = async () => {
    if (!editForm || editForm.start_time >= editForm.end_time) { toast.error('End time must be after start time'); return; }
    try {
      await axios.put(`${API}/api/exam-timetable/${editingId}`, editForm, { headers: hdrs() });
      toast.success('Updated'); cancelEdit(); fetchEntries();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this exam entry?')) return;
    try { await axios.delete(`${API}/api/exam-timetable/${id}`, { headers: hdrs() }); toast.success('Deleted'); fetchEntries(); } catch { toast.error('Failed'); }
  };

  // Group entries for semester-wise display
  const grouped = entries.reduce((acc, e) => {
    const k = `${e.stream}|||${e.semester}`;
    if (!acc[k]) acc[k] = { stream: e.stream, semester: e.semester, semester_type: e.semester_type, academic_year: e.academic_year, entries: [] };
    acc[k].entries.push(e);
    return acc;
  }, {});

  if (loading) return <div className="flex items-center justify-center h-full" data-testid="exam-loading"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="exam-timetable-page">
      {/* Header */}
      <div className="flex items-center justify-between print:mb-0">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 print:text-xl" data-testid="exam-page-title">
            <FileText className="w-7 h-7 text-blue-600 print:hidden" />
            Exam Timetable
          </h1>
          <p className="text-sm text-gray-500 mt-1 print:hidden">Schedule and manage final semester examinations</p>
        </div>
        <div className="flex gap-2 print:hidden">
          {!showForm && (
            <Button
              variant={studentView ? "default" : "outline"}
              onClick={() => setStudentView(!studentView)}
              className={`gap-2 ${studentView ? 'bg-green-600 hover:bg-green-700' : ''}`}
              data-testid="student-view-toggle"
            >
              <Eye className="w-4 h-4" /> {studentView ? 'Student View ON' : 'Student View'}
            </Button>
          )}
          <Button variant="outline" onClick={() => window.print()} className="gap-2" data-testid="exam-print-btn"><Printer className="w-4 h-4" /> Print</Button>
          {!showForm && !studentView && <Button onClick={() => { resetForm(); setShowForm(true); }} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="exam-add-btn"><Plus className="w-4 h-4" /> Schedule Exams</Button>}
        </div>
      </div>

      {/* Filters */}
      {!studentView && (
      <Card className="print:hidden" data-testid="exam-filters-card">
        <CardContent className="pt-4 pb-4">
          <div className="flex items-center gap-2 mb-3"><Filter className="w-4 h-4 text-gray-500" /><span className="text-sm font-medium text-gray-700">Filters</span></div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select value={filters.academic_year} onChange={e => setFilters(p => ({ ...p, academic_year: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-academic-year">
              <option value="">All Years</option>{genAcademicYears().map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={filters.semester_type} onChange={e => setFilters(p => ({ ...p, semester_type: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-semester-type">
              <option value="">All (Even/Odd)</option><option value="Even">Even Semester</option><option value="Odd">Odd Semester</option>
            </select>
            <select value={filters.stream} onChange={e => setFilters(p => ({ ...p, stream: e.target.value, semester: '' }))} className="border rounded-lg px-3 py-2 text-sm" data-testid="filter-stream">
              <option value="">All Streams</option>{STREAMS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
            <select value={filters.semester} onChange={e => setFilters(p => ({ ...p, semester: e.target.value }))} className="border rounded-lg px-3 py-2 text-sm" disabled={!filters.stream} data-testid="filter-semester">
              <option value="">All Semesters</option>{filters.stream && (SEMESTERS_BY_STREAM[filters.stream] || []).map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Bulk Scheduling Form */}
      {showForm && (
        <Card className="border-2 border-blue-200 print:hidden" data-testid="exam-form-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-600" />Schedule Exams</CardTitle>
              <Button variant="ghost" size="sm" onClick={resetForm} data-testid="exam-form-close"><X className="w-4 h-4" /></Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Header selector */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <div>
                <label className="block text-xs font-semibold text-blue-700 mb-1">Academic Year</label>
                <select value={header.academic_year} onChange={e => setHeader(p => ({ ...p, academic_year: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="form-academic-year">
                  {genAcademicYears().map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-blue-700 mb-1">Semester Type</label>
                <select value={header.semester_type} onChange={e => setHeader(p => ({ ...p, semester_type: e.target.value }))} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="form-semester-type">
                  <option value="Even">Even Semester</option><option value="Odd">Odd Semester</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-blue-700 mb-1">Stream</label>
                <select value={header.stream} onChange={e => handleStreamSemChange('stream', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" data-testid="form-stream">
                  <option value="">Select Stream</option>{STREAMS.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-blue-700 mb-1">Semester</label>
                <select value={header.semester} onChange={e => handleStreamSemChange('semester', e.target.value)} className="w-full border rounded-lg px-3 py-2 text-sm bg-white" disabled={!header.stream} data-testid="form-semester">
                  <option value="">Select Semester</option>{header.stream && (SEMESTERS_BY_STREAM[header.stream] || []).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>

            {/* Auto-populated subjects table */}
            {rows.length > 0 && (
              <>
                <div className="text-xs text-gray-500">{rows.length} subjects loaded — fill in exam details below</div>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-gray-100 text-gray-600 font-semibold">
                        <th className="px-3 py-2 text-left w-8">#</th>
                        <th className="px-3 py-2 text-left">Subject</th>
                        <th className="px-3 py-2 text-left">Date *</th>
                        <th className="px-3 py-2 text-left">Start</th>
                        <th className="px-3 py-2 text-left">End</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Venue</th>
                        <th className="px-3 py-2 text-left">Invigilator</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, idx) => (
                        <tr key={row.subject_id} className={`border-t ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-testid={`bulk-row-${idx}`}>
                          <td className="px-3 py-2 text-gray-400 font-medium">{idx + 1}</td>
                          <td className="px-3 py-2 font-medium text-gray-900 min-w-[180px]">
                            {row.subject_name}
                            <span className={`ml-1.5 text-[10px] px-1.5 py-0.5 rounded ${row.exam_type === 'Practical' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'}`}>{row.exam_type}</span>
                          </td>
                          <td className="px-3 py-2"><input type="date" value={row.exam_date} onChange={e => updateRow(idx, 'exam_date', e.target.value)} className="border rounded px-2 py-1.5 text-xs w-full" data-testid={`row-date-${idx}`} /></td>
                          <td className="px-3 py-2"><input type="time" value={row.start_time} onChange={e => updateRow(idx, 'start_time', e.target.value)} min="09:00" max="16:00" className="border rounded px-1.5 py-1.5 text-xs w-[90px]" data-testid={`row-start-${idx}`} /></td>
                          <td className="px-3 py-2"><input type="time" value={row.end_time} onChange={e => updateRow(idx, 'end_time', e.target.value)} min="09:00" max="16:00" className="border rounded px-1.5 py-1.5 text-xs w-[90px]" data-testid={`row-end-${idx}`} /></td>
                          <td className="px-3 py-2">
                            <select value={row.exam_type} onChange={e => updateRow(idx, 'exam_type', e.target.value)} className="border rounded px-1.5 py-1.5 text-xs" data-testid={`row-type-${idx}`}>
                              <option value="Theory">Theory</option><option value="Practical">Practical</option>
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select value={row.classroom_id} onChange={e => updateRow(idx, 'classroom_id', e.target.value)} className="border rounded px-1.5 py-1.5 text-xs w-full" data-testid={`row-venue-${idx}`}>
                              <option value="">--</option>{classrooms.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-2">
                            <select value={row.invigilator_id} onChange={e => updateRow(idx, 'invigilator_id', e.target.value)} className="border rounded px-1.5 py-1.5 text-xs w-full" data-testid={`row-invigilator-${idx}`}>
                              <option value="">--</option>{teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <p className="text-xs text-gray-500">{rows.filter(r => r.exam_date).length} of {rows.length} subjects have dates assigned</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={resetForm} data-testid="form-cancel-btn">Cancel</Button>
                    <Button onClick={handleBulkSubmit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="form-submit-btn">
                      <Save className="w-4 h-4" />{saving ? 'Saving...' : `Schedule ${rows.filter(r => r.exam_date).length} Exams`}
                    </Button>
                  </div>
                </div>
              </>
            )}
            {header.stream && !header.semester && <p className="text-sm text-gray-400 text-center py-6">Select a semester to load subjects</p>}
            {!header.stream && <p className="text-sm text-gray-400 text-center py-6">Select a stream and semester to begin</p>}
          </CardContent>
        </Card>
      )}

      {/* SEMESTER-WISE EXAM TIMETABLE VIEW — Shareable format */}
      {entries.length === 0 && !showForm ? (
        <Card data-testid="exam-empty-state">
          <CardContent className="py-16 text-center">
            <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-500">No exams scheduled</h3>
            <p className="text-sm text-gray-400 mt-1">Click "Schedule Exams" to start scheduling</p>
          </CardContent>
        </Card>
      ) : entries.length > 0 && (
        <div className="space-y-6" data-testid="exam-entries-list">
          {Object.keys(grouped).sort().map(key => {
            const group = grouped[key];
            const sortedEntries = group.entries.sort((a, b) => a.exam_date === b.exam_date ? a.start_time.localeCompare(b.start_time) : a.exam_date.localeCompare(b.exam_date));

            return (
              <div key={key} className="break-inside-avoid" data-testid={`exam-group-${group.stream}-${group.semester}`}>
                {/* Shareable timetable card */}
                <div className="border-2 border-gray-300 rounded-lg overflow-hidden bg-white">
                  {/* Header banner */}
                  <div className="bg-gradient-to-r from-blue-700 to-indigo-700 text-white px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold">{group.stream}</h2>
                        {editingTerm === key ? (
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="text"
                              value={termInput}
                              onChange={e => setTermInput(e.target.value)}
                              className="bg-white/20 border border-white/40 rounded px-2 py-1 text-sm text-white placeholder-blue-200 w-80"
                              placeholder="e.g. Even Semester Examination 2025-26"
                              autoFocus
                              data-testid={`term-input-${key}`}
                            />
                            <button onClick={() => { setTermTexts(prev => ({ ...prev, [key]: termInput })); setEditingTerm(null); }} className="p-1 bg-white/20 rounded hover:bg-white/30" data-testid={`term-save-${key}`}><Check className="w-4 h-4" /></button>
                            <button onClick={() => { setEditingTerm(null); }} className="p-1 bg-white/20 rounded hover:bg-white/30"><X className="w-4 h-4" /></button>
                          </div>
                        ) : (
                          <p className="text-blue-100 text-sm flex items-center gap-2">
                            {termTexts[key] || `${group.semester} — ${group.semester_type} Semester Examination`}
                            {!studentView && (
                              <button onClick={() => { setTermInput(termTexts[key] || `${group.semester} — ${group.semester_type} Semester Examination`); setEditingTerm(key); }} className="p-0.5 bg-white/10 rounded hover:bg-white/20" title="Edit exam term" data-testid={`term-edit-${key}`}><Edit2 className="w-3 h-3" /></button>
                            )}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-medium">Academic Year {group.academic_year}</div>
                        <div className="text-blue-200 text-xs">{sortedEntries.length} Subjects</div>
                      </div>
                    </div>
                  </div>

                  {/* Exam table */}
                  <table className="w-full text-sm" data-testid={`exam-table-${group.stream}-${group.semester}`}>
                    <thead>
                      <tr className="bg-gray-100 border-b-2 border-gray-300">
                        <th className="px-4 py-3 text-left font-semibold text-gray-700 w-8">Sr.</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Day</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Time</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Subject</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Venue</th>
                        <th className="px-4 py-3 text-left font-semibold text-gray-700">Invigilator</th>
                        {!studentView && <th className="px-4 py-3 text-left font-semibold text-gray-700 w-20 print:hidden">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEntries.map((entry, idx) => (
                        editingId === entry.id && editForm && !studentView ? (
                          <tr key={entry.id} className="border-b bg-blue-50" data-testid={`edit-row-${entry.id}`}>
                            <td className="px-4 py-2 text-gray-400">{idx+1}</td>
                            <td className="px-4 py-2"><input type="date" value={editForm.exam_date} onChange={e => updateEditField('exam_date', e.target.value)} className="border rounded px-2 py-1 text-xs" /></td>
                            <td className="px-4 py-2 text-xs text-gray-500">{fmtDay(editForm.exam_date)}</td>
                            <td className="px-4 py-2">
                              <div className="flex gap-1"><input type="time" value={editForm.start_time} onChange={e => updateEditField('start_time', e.target.value)} className="border rounded px-1 py-1 text-xs w-[80px]" /><input type="time" value={editForm.end_time} onChange={e => updateEditField('end_time', e.target.value)} className="border rounded px-1 py-1 text-xs w-[80px]" /></div>
                            </td>
                            <td className="px-4 py-2 font-medium text-xs">{entry.subject_name}</td>
                            <td className="px-4 py-2"><select value={editForm.exam_type} onChange={e => updateEditField('exam_type', e.target.value)} className="border rounded px-1 py-1 text-xs"><option value="Theory">Theory</option><option value="Practical">Practical</option></select></td>
                            <td className="px-4 py-2"><select value={editForm.classroom_id} onChange={e => updateEditField('classroom_id', e.target.value)} className="border rounded px-1 py-1 text-xs w-full"><option value="">--</option>{classrooms.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}</select></td>
                            <td className="px-4 py-2"><select value={editForm.invigilator_id} onChange={e => updateEditField('invigilator_id', e.target.value)} className="border rounded px-1 py-1 text-xs w-full"><option value="">--</option>{teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</select></td>
                            <td className="px-4 py-2"><div className="flex gap-1"><Button variant="ghost" size="sm" onClick={saveEdit} className="h-6 px-2 text-green-600 text-xs" data-testid={`save-edit-${entry.id}`}><Save className="w-3 h-3 mr-1" />Save</Button><Button variant="ghost" size="sm" onClick={cancelEdit} className="h-6 px-1 text-gray-400 text-xs"><X className="w-3 h-3" /></Button></div></td>
                          </tr>
                        ) : (
                          <tr key={entry.id} className={`border-b ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50/40 transition-colors`} data-testid={`exam-row-${entry.id}`}>
                            <td className="px-4 py-3 text-gray-500 font-medium">{idx+1}</td>
                            <td className="px-4 py-3 font-medium text-gray-900">{fmtDate(entry.exam_date)}</td>
                            <td className="px-4 py-3 text-gray-600">{fmtDay(entry.exam_date)}</td>
                            <td className="px-4 py-3 text-gray-700 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-gray-400 print:hidden" />{fmtTime(entry.start_time)} - {fmtTime(entry.end_time)}</span>
                            </td>
                            <td className="px-4 py-3 font-semibold text-gray-900">{entry.subject_name}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${entry.exam_type === 'Theory' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}`}>{entry.exam_type}</span>
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {entry.classroom_name ? <span className="inline-flex items-center gap-1"><Building className="w-3.5 h-3.5 text-gray-400 print:hidden" />{entry.classroom_name}</span> : <span className="text-gray-400">—</span>}
                            </td>
                            <td className="px-4 py-3 text-gray-700">
                              {entry.invigilator_name ? <span className="inline-flex items-center gap-1"><User className="w-3.5 h-3.5 text-gray-400 print:hidden" />{entry.invigilator_name}</span> : <span className="text-gray-400">—</span>}
                            </td>
                            {!studentView && (
                            <td className="px-4 py-3 print:hidden">
                              <div className="flex gap-0.5">
                                <Button variant="ghost" size="sm" onClick={() => startEdit(entry)} className="h-7 w-7 p-0 text-blue-600" data-testid={`edit-exam-${entry.id}`}><Edit2 className="w-3.5 h-3.5" /></Button>
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="h-7 w-7 p-0 text-red-500" data-testid={`delete-exam-${entry.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                              </div>
                            </td>
                            )}
                          </tr>
                        )
                      ))}
                    </tbody>
                  </table>

                  {/* Footer note */}
                  <div className="px-6 py-3 bg-gray-50 border-t text-xs text-gray-500 flex items-center justify-between">
                    <span>Examination timings: 9:00 AM to 4:00 PM | Reporting time: 30 minutes before exam</span>
                    {!studentView && <span>{sortedEntries.length} exams</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
};

export default ExamTimetable;
